import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request, API_BASE, ApiError } from './client.ts';
import { qk } from './keys.ts';
import type { Media, CreateMedia, CreateUrlMedia } from '@seencast/shared';
import type { ListEnvelope } from './client.ts';
import { getActiveOrgId } from './org-store.ts';
import type { ContentKind } from './templates.ts';

export interface MediaFilters {
  // Content-engine kinds (html_template/html_snippet) are filterable too (docs/09 §B).
  kind?: ContentKind;
  q?: string;
}

export function listMedia(filters: MediaFilters = {}): Promise<Media[]> {
  return request<ListEnvelope<Media>>('/media', { query: { ...filters } }).then((r) => r.items);
}

export function useMedia(filters: MediaFilters = {}) {
  return useQuery({
    queryKey: qk.media(filters),
    queryFn: () => listMedia(filters),
  });
}

/**
 * A `media_id → human name` resolver for live "Now playing" labels (Sprint 9).
 * The presence socket reports a media_id, usually without a name (architect
 * ruling), so the Studio resolves it against its own media library. Backed by the
 * media list query (shared cache); returns undefined for an unknown/deleted id so
 * the caller can fall back. Loading the library here is cheap for pilot fleets.
 */
export function useMediaNameResolver(): (mediaId: string) => string | undefined {
  const media = useMedia();
  return useMemo(() => {
    const byId = new Map<string, string>();
    for (const m of media.data ?? []) if (m.filename) byId.set(m.id, m.filename);
    return (id: string) => byId.get(id);
  }, [media.data]);
}

export interface CreateMediaResponse {
  media_id: string;
  upload_url: string;
}

export function createMedia(body: CreateMedia): Promise<CreateMediaResponse> {
  return request<CreateMediaResponse>('/media', { method: 'POST', body });
}

export function completeMedia(id: string): Promise<Media> {
  return request<Media>(`/media/${id}/complete`, { method: 'POST' });
}

export function useAddUrlMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUrlMedia) => request<Media>('/media/url', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media'] }),
  });
}

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    // Throws ApiError(409, 'referenced') when a playlist still uses this media.
    mutationFn: (id: string) => request<void>(`/media/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media'] }),
  });
}

/** The API origin CMS-authored urls resolve against: relative (`/v1`, vite-proxied) in
 * dev/test, the deployed API worker's absolute origin in production. Read live (not
 * cached at module scope) so both helpers below pick up VITE_API_BASE at call time —
 * required for tests that stub it per-case and harmless in a real build (the env var
 * is fixed for the life of the process either way). */
function apiBase(): string {
  return (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '/v1';
}

/** URL to stream/preview a media file in the CMS (session-authed). Pass
 * `{ thumb: true }` to request the generated thumbnail — the server falls back to
 * the original file when no thumb exists yet (docs/03 §Media). */
export function mediaFileUrl(id: string, opts?: { thumb?: boolean }): string {
  const path = `${apiBase()}/media/${id}/file`;
  return opts?.thumb ? `${path}?thumb=1` : path;
}

/**
 * Rewrite a session-fetchable relative media url (`/v1/media/:id/file[?v=…]`) to an
 * absolute API-origin url. Both the CMS's own true-preview manifest builder
 * (lib/preview-manifest.ts) and the backend's `toSessionManifest` (the screen
 * "Preview now" manifest) emit urls in this relative shape, matching each other by
 * design — but the true-preview engine renders media directly as `<img>`/`<video>`/
 * `<iframe>` src, bypassing `request()`, so a relative url resolves against the CMS's
 * OWN origin (app.seencast.app) in a deployed build, not the API's. This is the one
 * place that gap is closed, for BOTH preview surfaces (EnginePreview wires it into
 * player-core's `PlainUrlMediaSource`). Already-absolute urls — external `url`-kind
 * media, blob/data urls — pass through unchanged.
 */
export function resolvePreviewMediaUrl(url: string): string {
  return url.startsWith('/v1/') ? `${apiBase()}${url.slice(3)}` : url;
}

/**
 * PUT the generated WebP thumbnail (docs/10 #2.2). Not JSON, so it bypasses
 * `request()` — mirrors its auth (session cookie + org header) with a binary body.
 * Callers treat any rejection as best-effort failure (see lib/thumbs.ts callers).
 */
export async function putMediaThumb(id: string, blob: Blob): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': blob.type || 'image/webp' };
  const orgId = getActiveOrgId();
  if (orgId) headers['X-Org-Id'] = orgId;
  const res = await fetch(`${API_BASE}/media/${id}/thumb`, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: blob,
  });
  if (!res.ok) throw new ApiError(res.status, 'thumb_failed', 'Thumbnail upload failed');
}
